import React, { useState, useEffect, useCallback } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import LoginScreen from './components/auth/LoginScreen.jsx';
import ForgotPasswordScreen from './components/auth/ForgotPasswordScreen.jsx';
import AuthLayout from './components/auth/AuthLayout.jsx';
import RegistrationForm from './components/pages/UserPages/RegistrationForm.jsx';
import Dashboard from './components/dashboard/Dashboard.jsx';
import { useAuth, useHotelManagement } from './hooks';
import { useAppState } from './hooks/useAppState';
import { useReservationSuccess } from './hooks/useReservationSuccess';
import { useDashboardData } from './queries/queries';
import { createQueryClient } from './queries/queryClient';
import { loadRecaptcha } from './utils/recaptcha';

// Import test utility for development
if (process.env.NODE_ENV === 'development') {
  import('./utils/testNotification');
}

// Load reCAPTCHA on app initialization
loadRecaptcha().catch(err => {
  console.warn('ReCAPTCHA loading failed:', err);
});

// Create React Query client with enhanced configuration
const queryClient = createQueryClient();

const TeamRezyPlatformContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Centralized state management
  const { state, actions } = useAppState();

  // Authentication hook
  const {
    currentUser,
    currentView,
    isLoading,
    authInitialized,
    setCurrentView,
    setCurrentUser,
    handleRegistrationSuccess,
    handleAccountUpdateSuccess
  } = useAuth();

  // Hotel management hook
  const {
    showHotelSearch,
    hotelSearchData,
    hotelConfirmation,
    preservedReservationState,
    handleHotelSearch,
    setPreservedReservationState,
    setShowHotelSearch,
    setHotelSearchData,
    setHotelConfirmation
  } = useHotelManagement();

  // Reservation success hook
  const { reservationSuccess, setReservationSuccess } = useReservationSuccess();

  // Local state
  const [showRegistration, setShowRegistration] = useState(false);

  // Initialize dashboard data management
  const dashboardData = useDashboardData(currentUser, {
    autoFetchVenues: !!currentUser,
    autoFetchReservations: !!currentUser,
    enableRealtime: true
  });
  
  // Close registration modal when user signs in
  React.useEffect(() => {
    if (showRegistration && currentUser) {
      setShowRegistration(false);
    }
  }, [currentUser, showRegistration]);

  // Handle redirect to account tab after login (for password reset flow)
  React.useEffect(() => {
    if (currentUser) {
      const shouldRedirect = sessionStorage.getItem('redirectToAccountTab');
      if (shouldRedirect === 'true') {
        // Clear the flag
        sessionStorage.removeItem('redirectToAccountTab');
        // Navigate to account tab
        actions.setActiveTab('account');
        // Clear any remaining hash
        window.location.hash = '';
      }
    }
  }, [currentUser, actions]);

  // Handle routing based on URL path and hash
  React.useEffect(() => {
    const path = location.pathname;
    const hash = window.location.hash;

    // Handle password reset hash - navigate to User Account tab
    if (hash === '#reset-password') {
      if (currentUser) {
        // User is logged in, navigate to account tab
        actions.setActiveTab('account');
        // Clear the hash after using it
        window.location.hash = '';
      } else {
        // User not logged in yet, store intent in sessionStorage
        sessionStorage.setItem('redirectToAccountTab', 'true');
      }
    }

    // Handle /register route
    if (path === '/register' && !currentUser) {
      setShowRegistration(true);
      setCurrentView('login');
    }

    // Handle /login route
    if (path === '/login' && !currentUser) {
      setShowRegistration(false);
      setCurrentView('login');
    }

    // Handle /reservations route
    if (path === '/reservations' && currentUser) {
      actions.setActiveTab('reservations');
      navigate('/');
    }

    // If user is logged in and on login/register, redirect to dashboard
    if (currentUser && (path === '/login' || path === '/register')) {
      navigate('/');
    }
  }, [location.pathname, currentUser, setCurrentView, navigate, actions]);

  // Get data from dashboard data hook (fallback to empty arrays)
  const venues = dashboardData.data.venues || [];
  const reservations = dashboardData.data.reservations || [];

  // Navigation state
  const activeTab = state.navigation.activeTab;
  const setActiveTab = useCallback((tab) => actions.setActiveTab(tab), [actions]);

  // Migrate mobile menu to centralized state
  const mobileMenuOpen = state.ui.mobileMenuOpen;
  const setMobileMenuOpen = useCallback((open) => actions.setMobileMenuOpen(open), [actions]);

  // Migrate desktop state to centralized state
  const isDesktop = state.ui.isDesktop;
  const setIsDesktop = useCallback((desktop) => actions.setIsDesktop(desktop), [actions]);

  // Set initial desktop state
  useEffect(() => {
    setIsDesktop(window.innerWidth >= 1024);
  }, [setIsDesktop]);

  // Close mobile menu when clicking outside or on resize
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop) {
        setMobileMenuOpen(false);
      }
    };

    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.mobile-menu-button')) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [mobileMenuOpen, setIsDesktop, setMobileMenuOpen]);

  // Update legacy actions to use new dashboard data hooks
  const legacyActions = {
    ...actions,
    setVenues: (venues) => {
      // Legacy support - data is now managed by hooks
      actions.setVenues(venues);
    },
    setReservations: (reservations) => {
      // Legacy support - data is now managed by hooks
      actions.setReservations(reservations);
    }
  };

  // Hotel confirmation logic
  if (hotelConfirmation) {
    console.log('🏨 TOP LEVEL Showing hotel confirmation screen');
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2 text-green-900">
            Hotel Search Test Complete
          </h2>
          <p className="text-gray-600 mb-4">{hotelConfirmation.message}</p>
          <button
            onClick={() => setHotelConfirmation(null)}
            className="bg-navy-600 text-white px-6 py-3 rounded-md hover:bg-navy-800 font-medium"
          >
            Back to App
          </button>
        </div>
      </div>
    );
  }

  // Show loading spinner while checking session and auth initialization
  if (isLoading || !authInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-600 to-burgundy-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600 mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Close registration modal if user is signed in
  if (showRegistration && !currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-600 to-burgundy-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg relative">
          <button
            onClick={() => setShowRegistration(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            style={{ border: 'none' }}
            aria-label="Close registration form"
          >
            &times;
          </button>
          <RegistrationForm
            onSuccess={handleRegistrationSuccess}
            onBackToLogin={() => setShowRegistration(false)}
          />
        </div>
      </div>
    );
  }

  if (currentView === 'login') {
    return (
      <AuthLayout>
        <LoginScreen
          onLoginSuccess={(user) => {
            setCurrentUser(user);
            setCurrentView('dashboard');
          }}
          onShowRegistration={() => setShowRegistration(true)}
          onShowForgotPassword={() => setCurrentView('forgotPassword')}
        />
      </AuthLayout>
    );
  }

  if (currentView === 'forgotPassword') {
    return (
      <AuthLayout>
        <ForgotPasswordScreen
          onBackToLogin={() => setCurrentView('login')}
        />
      </AuthLayout>
    );
  }

  return (
    <Dashboard
      currentUser={currentUser}
      reservations={reservations}
      venues={venues}
      mobileMenuOpen={mobileMenuOpen}
      setMobileMenuOpen={setMobileMenuOpen}
      isDesktop={isDesktop}
      setIsDesktop={setIsDesktop}
      showHotelSearch={showHotelSearch}
      hotelSearchData={hotelSearchData}
      preservedReservationState={preservedReservationState}
      setPreservedReservationState={setPreservedReservationState}
      handleHotelSearch={handleHotelSearch}
      setShowHotelSearch={setShowHotelSearch}
      setHotelSearchData={setHotelSearchData}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      reservationSuccess={reservationSuccess}
      setReservationSuccess={setReservationSuccess}
      handleAccountUpdateSuccess={handleAccountUpdateSuccess}
      actions={legacyActions}
      dashboardData={dashboardData}
    />
  );
};

// Wrapper component that provides QueryClient
const TeamRezyPlatform = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TeamRezyPlatformContent />
      {/* React Query DevTools - only shows in development */}
      <ReactQueryDevtools initialIsOpen={false} />
      {/* Global toast notifications */}
      <Toaster />
    </QueryClientProvider>
  );
};

export default TeamRezyPlatform;
